"use client";

import { Check, List, Share, Text, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildShareOnXUrl } from "./build-share-text";
import {
  type StoryTOCItem,
  TOCItemsContainer,
  useActiveAnchorId,
} from "./story-toc";

const MIN_SECTIONS_FOR_TOC = 4;
// Matches Tailwind's `lg` breakpoint — the toolbar uses `lg:hidden` to
// reveal the desktop sidebar, so the sheet must auto-close if the viewport
// crosses this threshold while open (e.g. tablet rotation).
const DESKTOP_BREAKPOINT_QUERY = "(min-width: 1024px)";

interface MobileStoryToolbarProps {
  tocItems: StoryTOCItem[];
  deck: string;
  headline: string;
  storyUrl: string;
}

export function MobileStoryToolbar({
  tocItems,
  deck,
  headline,
  storyUrl,
}: MobileStoryToolbarProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const activeId = useActiveAnchorId();
  const activeIdx = tocItems.findIndex((item) => item.id === activeId);
  const position = activeIdx >= 0 ? activeIdx + 1 : null;
  const total = tocItems.length;
  const showToc = tocItems.length >= MIN_SECTIONS_FOR_TOC;

  const shareOnXUrl = buildShareOnXUrl({ headline, deck, storyUrl });

  const handleShare = useCallback(async () => {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: headline,
          text: deck,
          url: storyUrl,
        });
        return;
      } catch (error) {
        // User dismissed the native share sheet — nothing else to do.
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        // Any other rejection (policy, permission, platform error) should
        // fall through to the clipboard path so the action is never a no-op.
      }
    }
    try {
      await navigator.clipboard.writeText(storyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard write can fail silently in some contexts
    }
  }, [deck, headline, storyUrl]);

  const closeSheet = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Close the sheet when the viewport crosses into desktop width so the
  // body-scroll lock does not outlive the sheet visibility (`lg:hidden`).
  useEffect(() => {
    if (!open) {
      return;
    }
    const mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
    if (mediaQuery.matches) {
      setOpen(false);
      return;
    }
    function onChange(event: MediaQueryListEvent) {
      if (event.matches) {
        setOpen(false);
      }
    }
    mediaQuery.addEventListener("change", onChange);
    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }, [open]);

  return (
    <>
      <div
        className="fixed right-3 bottom-6 z-50 flex flex-col gap-2 rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg)]/90 p-2 shadow-lg backdrop-blur-md lg:hidden"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        {showToc ? (
          <button
            aria-controls="story-toc-mobile-sheet"
            aria-expanded={open}
            aria-label={
              position === null
                ? `Table of contents, ${String(total)} sections`
                : `Table of contents, section ${String(position)} of ${String(total)}`
            }
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--explore-text-primary)] text-[var(--explore-bg-card)] transition-opacity active:opacity-80"
            onClick={() => setOpen(true)}
            ref={triggerRef}
            type="button"
          >
            <List aria-hidden="true" className="size-4" />
          </button>
        ) : null}
        <a
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--explore-text-primary)] text-[var(--explore-bg)] no-underline transition-opacity active:opacity-80"
          href={shareOnXUrl}
          rel="noopener noreferrer"
          target="_blank"
          title="Share on X"
        >
          <svg
            aria-hidden="true"
            className="h-3.5 w-3.5"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          <span className="sr-only">Share on X</span>
        </a>
        <button
          aria-label={copied ? "Link copied" : "Share story"}
          className={`flex h-11 w-11 items-center justify-center rounded-full transition-all active:opacity-80 ${
            copied
              ? "bg-[var(--explore-accent-teal)] text-[var(--explore-on-accent-text)]"
              : "bg-[var(--explore-bg-card)] text-[var(--explore-text-secondary)]"
          }`}
          onClick={handleShare}
          type="button"
        >
          {copied ? (
            <Check aria-hidden="true" className="size-4" />
          ) : (
            <Share aria-hidden="true" className="size-4" />
          )}
        </button>
      </div>
      {open ? (
        <MobileStoryTocSheet items={tocItems} onClose={closeSheet} />
      ) : null}
    </>
  );
}

function MobileStoryTocSheet({
  items,
  onClose,
}: {
  items: StoryTOCItem[];
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const focusables = Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    const firstFocusable = focusables[0];
    const lastFocusable = focusables.at(-1);

    firstFocusable?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || focusables.length === 0) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && active === firstFocusable) {
        event.preventDefault();
        lastFocusable?.focus();
      } else if (!event.shiftKey && active === lastFocusable) {
        event.preventDefault();
        firstFocusable?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      aria-labelledby="story-toc-sheet-title"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex flex-col lg:hidden"
      id="story-toc-mobile-sheet"
      ref={dialogRef}
      role="dialog"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div
        aria-hidden="true"
        className="fade-in absolute inset-0 animate-in bg-[var(--explore-overlay-backdrop)] duration-200"
        onClick={onClose}
      />
      <div className="slide-in-from-bottom relative z-10 mt-auto max-h-[80vh] animate-in overflow-hidden rounded-t-2xl bg-[var(--explore-bg-card)] shadow-[var(--explore-shadow-sheet)] duration-300 ease-out">
        <div className="flex items-center gap-3 px-5 pt-4 pb-2">
          <span
            aria-hidden="true"
            className="inline-flex size-7 items-center justify-center rounded-full bg-[var(--explore-bg-warm)] text-[var(--explore-text-secondary)]"
          >
            <Text className="size-3.5" />
          </span>
          <h3
            className="flex-1 font-semibold text-[0.95rem] text-[var(--explore-text-primary)]"
            id="story-toc-sheet-title"
          >
            On this story
          </h3>
          <button
            aria-label="Close table of contents"
            className="inline-flex size-8 items-center justify-center rounded-full text-[var(--explore-text-muted)] transition-colors hover:bg-[var(--explore-bg-warm)] hover:text-[var(--explore-text-primary)]"
            onClick={onClose}
            type="button"
          >
            <X className="size-4" />
          </button>
        </div>
        <nav
          aria-label="Table of contents"
          className="max-h-[65vh] overflow-y-auto px-3 pb-6"
        >
          <TOCItemsContainer items={items} onItemClick={onClose} />
        </nav>
      </div>
    </div>
  );
}
